import React from "react";
import { Box, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";

interface HeatmapData {
  date: string;
  count: number;
}

interface ContributionHeatMapWeekProps {
  /** Array of data for the week. Should contain 7 entries (Monday to Sunday) */
  weekData: HeatmapData[];
  /** Size of each cell in pixels */
  cellSize?: number;
  /** Gap between cells in pixels */
  gap?: number;
  /** Callback when a cell is clicked */
  onCellClick?: (date: string) => void;
}

const WeekContainer = styled(Box)<{
  cellSize: number;
  gap: number;
}>(({ cellSize, gap }) => ({
  display: "flex",
  flexDirection: "column",
  gap: `${gap}px`,
  alignItems: "center",
}));

const Cell = styled("div")<{
  level: number;
  cellSize: number;
  isToday?: boolean;
  isClickable?: boolean;
}>(({ theme, level, cellSize, isToday, isClickable }) => ({
  width: `${cellSize}px`,
  height: `${cellSize}px`,
  borderRadius: "2px",
  cursor: isClickable ? "pointer" : "default",
  border: isToday
    ? `2px solid ${theme.palette.primary.main}`
    : "1px solid #e1e4e8",
  backgroundColor:
    level === 0
      ? theme.palette.grey[200]
      : level === 1
      ? theme.palette.success.light
      : level === 2
      ? theme.palette.success.main
      : level === 3
      ? theme.palette.success.dark
      : theme.palette.success.dark,
  transition: "all 0.2s ease",
  "&:hover": isClickable
    ? {
        transform: "scale(1.1)",
        zIndex: 1,
        boxShadow: theme.shadows[2],
      }
    : {},
}));

const ContributionHeatMapWeek: React.FC<ContributionHeatMapWeekProps> = ({
  weekData,
  cellSize = 20,
  gap = 2,
  onCellClick,
}) => {
  const today = new Date().toISOString().split("T")[0];

  const formatTooltip = (date: string, count: number) => {
    const dateObj = new Date(date);
    const formattedDate = dateObj.toLocaleDateString("fr-FR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    if (count === 0) {
      return `Aucune contribution le ${formattedDate}`;
    } else if (count === 1) {
      return `1 contribution le ${formattedDate}`;
    } else {
      return `${count} contributions le ${formattedDate}`;
    }
  };

  const getContributionLevel = (count: number): number => {
    if (count === 0) return 0;
    if (count <= 2) return 1;
    if (count <= 5) return 2;
    return 3;
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <WeekContainer cellSize={cellSize} gap={gap}>
        {weekData.map((data, index) => {
          const level = getContributionLevel(data.count);
          const isToday = data.date === today;

          return (
            <Tooltip
              key={data.date}
              title={formatTooltip(data.date, data.count)}
              arrow
              placement="right"
            >
              <Cell
                level={level}
                cellSize={cellSize}
                isToday={isToday}
                isClickable={!!onCellClick}
                onClick={() => onCellClick?.(data.date)}
              />
            </Tooltip>
          );
        })}
      </WeekContainer>
    </Box>
  );
};

export default ContributionHeatMapWeek;
